import { ApplicationRef, inject, Injectable, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, filter, first, interval } from 'rxjs';

/**
 * PwaUpdateService
 *
 * Handles Angular service worker version lifecycle:
 * - Detects when a new app version is available
 * - Provides a signal-driven flag for the UI to show an update banner
 * - Applies the update by reloading the page
 * - Periodically polls for updates (every 6 hours when app is stable)
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);

  /** `true` when a new version of the app is ready to be activated */
  readonly updateAvailable = signal(false);

  constructor() {
    if (!this.swUpdate.isEnabled) return;

    // Listen for version-ready events
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        this.updateAvailable.set(true);
      });

    // Poll for updates once the app is stable, then every 6 hours
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable));
    const everySevenHours$ = interval(6 * 60 * 60 * 1000);

    concat(appIsStable$, everySevenHours$).subscribe(() => {
      void this.swUpdate.checkForUpdate();
    });
  }

  /**
   * Activates the waiting service worker and reloads the page.
   * Call this when the user clicks "Atualizar" in the update banner.
   */
  async applyUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) return;

    try {
      await this.swUpdate.activateUpdate();
    } finally {
      // Always reload even if activateUpdate throws — the SW may already be active
      document.location.reload();
    }
  }
}
